import type { DocumentType } from '@/types/health'

function toFourDigitYear(year: string): string {
  if (year.length === 4) {
    return year
  }

  const numericYear = Number.parseInt(year, 10)
  if (Number.isNaN(numericYear)) {
    return year
  }

  return numericYear >= 70 ? `19${year}` : `20${year.padStart(2, '0')}`
}

function buildIsoDate(year: string, month: string, day: string): string | null {
  const isoDate = `${toFourDigitYear(year)}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  const date = new Date(`${isoDate}T00:00:00Z`)

  if (Number.isNaN(date.getTime())) {
    return null
  }

  if (date.toISOString().slice(0, 10) !== isoDate) {
    return null
  }

  return isoDate
}

export function normalizeOptionalMedicalDate(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }

  const trimmedValue = value.trim()
  if (!trimmedValue) {
    return null
  }

  const isoMatch = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (isoMatch) {
    return buildIsoDate(isoMatch[1], isoMatch[2], isoMatch[3])
  }

  const europeanMatch = trimmedValue.match(/^(\d{2})[./-](\d{2})[./-](\d{2,4})$/)
  if (europeanMatch) {
    return buildIsoDate(europeanMatch[3], europeanMatch[2], europeanMatch[1])
  }

  const textEmbeddedMatch = trimmedValue.match(/\b(\d{2})[./-](\d{2})[./-](\d{2,4})\b/)
  if (textEmbeddedMatch) {
    return buildIsoDate(textEmbeddedMatch[3], textEmbeddedMatch[2], textEmbeddedMatch[1])
  }

  return null
}

export function extractDateFromFileName(fileName: string): string | null {
  const normalizedFileName = fileName.replace(/_/g, ' ')

  const leadingDateMatch = normalizedFileName.match(/(?:^|[^\d])(\d{2,4})[.\-_/](\d{2})[.\-_/](\d{2,4})(?:[^\d]|$)/)
  if (!leadingDateMatch) {
    return null
  }

  const first = leadingDateMatch[1]
  const second = leadingDateMatch[2]
  const third = leadingDateMatch[3]

  if (first.length === 4) {
    return buildIsoDate(first, second, third)
  }

  if (third.length === 4) {
    return buildIsoDate(third, second, first)
  }

  const firstNumber = Number.parseInt(first, 10)
  const secondNumber = Number.parseInt(second, 10)
  const thirdNumber = Number.parseInt(third, 10)

  if (
    !Number.isNaN(firstNumber) &&
    !Number.isNaN(secondNumber) &&
    !Number.isNaN(thirdNumber) &&
    firstNumber >= 20 &&
    firstNumber <= 39 &&
    secondNumber >= 1 &&
    secondNumber <= 12 &&
    thirdNumber >= 1 &&
    thirdNumber <= 31
  ) {
    return buildIsoDate(first, second, third) || buildIsoDate(third, second, first)
  }

  return buildIsoDate(third, second, first) || buildIsoDate(first, second, third)
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10)
}

function preferFilenameDate(candidateDate: string | null, fileNameDate: string | null): string | null {
  if (!fileNameDate) {
    return candidateDate
  }

  if (!candidateDate) {
    return fileNameDate
  }

  if (candidateDate > todayIsoDate()) {
    return fileNameDate
  }

  if (candidateDate === todayIsoDate() && fileNameDate !== todayIsoDate()) {
    return fileNameDate
  }

  return candidateDate
}

export function resolveAnalysisDates(params: {
  fileName: string
  typeDocument?: DocumentType | null
  dateDocument?: unknown
  datePrelevement?: unknown
}): {
  dateDocument: string
  datePrelevement: string | null
  storedDocumentDate: string
} {
  const fileNameDate = extractDateFromFileName(params.fileName)
  const normalizedDateDocument = preferFilenameDate(
    normalizeOptionalMedicalDate(params.dateDocument),
    fileNameDate
  )
  const normalizedDatePrelevement = preferFilenameDate(
    normalizeOptionalMedicalDate(params.datePrelevement),
    params.typeDocument === 'blood_test' ? fileNameDate : null
  )

  const resolvedDocumentDate = normalizedDateDocument || normalizedDatePrelevement || fileNameDate || todayIsoDate()
  const resolvedDatePrelevement =
    params.typeDocument === 'blood_test'
      ? normalizedDatePrelevement || fileNameDate || resolvedDocumentDate
      : normalizedDatePrelevement

  const storedDocumentDate =
    params.typeDocument === 'blood_test'
      ? resolvedDatePrelevement || resolvedDocumentDate
      : resolvedDocumentDate

  return {
    dateDocument: resolvedDocumentDate,
    datePrelevement: resolvedDatePrelevement,
    storedDocumentDate
  }
}
